import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

import type { CheckAccessResult } from '@/api/apiClient/auth';
import type { User } from 'firebase/auth';

import * as authApi from '@/api/apiClient/auth';
import { getFirebase } from '@/providers/firebase';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const isAuthenticated = computed(() => user.value !== null);
  const isInitializing = ref(true);
  const isAdmin = ref(false);

  /**
   * Log in a user with email and password
   *
   * @param email - User email address
   * @param password - User password
   */
  const login = async (email: string, password: string) => {
    const credential = await authApi.login(email, password);
    user.value = credential.user;
  };

  /**
   * Sign up a new user with email and password
   *
   * @param email - User email address
   * @param password - User password
   */
  const signup = async (email: string, password: string) => {
    const credential = await authApi.signup(email, password);
    user.value = credential.user;
  };

  /**
   * Log in a user with Google
   */
  const loginWithGoogle = async () => {
    const credential = await authApi.loginWithGoogle();
    user.value = credential.user;
  };

  /**
   * Log out the current user
   */
  const logout = async () => {
    await authApi.logout();
    user.value = null;
    isAdmin.value = false;
  };

  /**
   * Initialize auth state from Firebase
   * Waits for Firebase to restore auth state from localStorage before resolving.
   * Also calls verifyAccess() to populate isAdmin and registers for ongoing
   * auth state changes (e.g., cross-tab login/logout).
   */
  const initialize = async () => {
    try {
      const firebase = getFirebase();
      const currentUser = await firebase.waitForAuthReady();
      user.value = currentUser;

      if (currentUser != null) {
        await verifyAccess();
      }

      firebase.onAuthStateChange(async (changedUser) => {
        user.value = changedUser;
        if (changedUser != null) {
          try {
            await verifyAccess();
          } catch (error) {
            console.error(
              'Failed to verify access on auth state change:',
              error,
            );
            isAdmin.value = false;
          }
        } else {
          isAdmin.value = false;
        }
      });
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      user.value = null;
    } finally {
      isInitializing.value = false;
    }
  };

  /**
   * Verify that the current user has access to this deployment
   *
   * @returns CheckAccessResult if user is logged in, null otherwise
   */
  const verifyAccess = async (): Promise<CheckAccessResult | null> => {
    if (!user.value) {
      return null;
    }
    const result = await authApi.checkAccess();
    isAdmin.value = result?.isAdmin ?? false;
    return result;
  };

  /**
   * Force refresh the user's ID token to get updated claims
   * This is needed after operations that modify the user's custom claims (like invite redemption)
   */
  const refreshToken = async () => {
    if (user.value == null) {
      return;
    }
    // Force token refresh to get updated custom claims
    await user.value.getIdToken(true);
  };

  return {
    user,
    isAuthenticated,
    isInitializing,
    isAdmin,
    login,
    signup,
    loginWithGoogle,
    logout,
    initialize,
    verifyAccess,
    refreshToken,
  };
});
